import { api, endpoints } from "api/api";
import { AxiosPromise, AxiosResponse } from "axios";
import { ResourceTabIcon } from "pages/CourseResources/ClientIconsDropdown";
import { ResourceTabFormValues } from "pages/CourseResources/TabModals/ResourceTabForm";

const getByCourseId = (courseId: number): AxiosPromise<CourseResourceTabApi[]> => {
  return api.get<CourseResourceTabApi[]>(endpoints.COURSE_RESOURCES.GET_BY_COURSEID(courseId));
};

const getTabById = (courseId: number, courseResourceTabId: number): AxiosPromise<CourseResourceTabApi> => {
  return api.get<CourseResourceTabApi>(endpoints.COURSE_RESOURCES.GET_TAB_BY_ID(courseId, courseResourceTabId));
};

const createTab = (courseId: number, body: ResourceTabFormValues): AxiosPromise<number> => {
  return api.post<number>(endpoints.COURSE_RESOURCES.CREATE_TAB(courseId), body);
};

const updateTab = (courseId: number, courseResourceTabId: number, body: ResourceTabFormValues): AxiosPromise<void> => {
  return api.put<void>(endpoints.COURSE_RESOURCES.UPDATE_TAB(courseId, courseResourceTabId), body);
};
const deleteTab = (courseId: number, courseResourceTabId: number): AxiosPromise<void> => {
  return api.delete<void>(endpoints.COURSE_RESOURCES.DELETE_TAB(courseId, courseResourceTabId));
};

const updateLinkResource = (courseId: number, resourceId: number, body: UpdateLinkResourceBody): AxiosPromise<void> => {
  return api.put<void>(endpoints.COURSE_RESOURCES.UPDATE_RESOURCE_LINK(courseId, resourceId), body);
};

const updateFileResource = (courseId: number, resourceId: number, body: UpdateFileResourceBody): AxiosPromise<void> => {
  const formData = new FormData();

  formData.append("resource", body.resource);
  formData.append("name", body.name);
  formData.append("description", body.description);
  return api.put<void>(endpoints.COURSE_RESOURCES.UPDATE_RESOURCE_FILE(courseId, resourceId), formData);
};

const createLinkResource = (
  courseId: number,
  courseResourceTabId: number,
  body: CreateLinkResourceBody,
): AxiosPromise<number> => {
  return api.post<number>(endpoints.COURSE_RESOURCES.CREATE_LINK_RESOURCE(courseId, courseResourceTabId), body);
};

const createDocumentResource = (
  courseId: number,
  courseResourceTabId: number,
  body: CreateDocumentResourceBody,
): AxiosPromise<number> => {
  return api.post<number>(endpoints.COURSE_RESOURCES.CREATE_DOCUMENT_RESOURCE(courseId, courseResourceTabId), body);
};

const createFileResource = (
  courseId: number,
  courseResourceTabId: number,
  body: CreateFileResourceBody,
): AxiosPromise<number> => {
  const formData = new FormData();

  formData.append("resource", body.resource);
  formData.append("name", body.name);
  formData.append("description", body.description);
  formData.append("folder", body.folder);
  formData.append("sendEmail", body.sendEmail.toString());
  return api.post<number>(endpoints.COURSE_RESOURCES.CREATE_FILE_RESOURCE(courseId, courseResourceTabId), formData);
};

const deleteResource = (courseId: number, resourceId: number): AxiosPromise<void> => {
  return api.delete<void>(endpoints.COURSE_RESOURCES.DELETE(courseId, resourceId));
};

const updateFolders = (courseId: number, folders: Folder[]): AxiosPromise<void> => {
  return api.put<void>(endpoints.COURSE_RESOURCES.UPDATE_FOLDERS(courseId), { folders });
};

const download = (courseId: number, resourceId: number): AxiosPromise<AxiosResponse<Blob>> => {
  return api.get(endpoints.COURSE_RESOURCES.DOWNLOAD_RESOURCE(courseId, resourceId), {
    responseType: "blob",
  });
};

const updatePaymentPolicy = (courseId: number, requireFullPayment: boolean): AxiosPromise<void> => {
  return api.put<void>(endpoints.COURSE_RESOURCES.UPDATE_PAYMENT_POLICY(courseId), { requireFullPayment });
};

export const courseResourcesApi = {
  getByCourseId,
  getTabById,
  deleteTab,
  createLinkResource,
  createDocumentResource,
  updateTab,
  createTab,
  createFileResource,
  updateFileResource,
  delete: deleteResource,
  updateLinkResource,
  updateFolders,
  download,
  updatePaymentPolicy,
};

export interface Folder {
  name: string;
  resources: Resource[];
}

export interface Resource {
  id: number;
  name: string;
}

export interface CreateFileResourceBody extends BaseCreateResourceBody {
  resource: File;
}
export interface CreateLinkResourceBody extends BaseCreateResourceBody {
  type: number;
  link: string;
}

export interface CreateDocumentResourceBody extends BaseCreateResourceBody {
  document: number;
}

export interface BaseCreateResourceBody {
  name: string;
  description: string;
  folder: string;
  sendEmail: boolean;
}

export interface UpdateLinkResourceBody {
  name: string;
  description: string;
  link: string;
}
export interface UpdateFileResourceBody {
  name: string;
  description: string;
  resource: File;
}

export interface CourseResourceApi {
  id: number;
  name: string;
  link: string;
  description?: string | null;
  folder?: string | null;
  type: number;
  createDate: string;
  order: number;
  folderOrder: number;
}

export interface CourseResourceTabApi {
  id: number;
  name: string;
  icon: ResourceTabIcon;
  resources: CourseResourceApi[];
}
